import {
  UploadSaleInvoiceOptions,
  UploadOtherDocumentsOptions,
  InvoiceTemplate,
  MatchedBy,
  UploadOtherDocumentTypes
} from '@/types/document';
import { defaultTheme } from '@/styles/themes/default';
import {
  GenericSelectOption,
  SORTING_TYPE_DOCUMENT_DATE_ASC,
  SORTING_TYPE_DOCUMENT_DATE_DESC
} from './general_contants';

export const AUTOMATION_PREFIX_PRODUCTION = 'p+';
export const AUTOMATION_PREFIX_STAGING = 's+';
export const AUTOMATION_SALES_TAG = 's+';
export const AUTOMATION_PURCHASE_TAG = 'p+';
export const AUTOMATION_SUFFIX_PRODUCTION = '@companio.co';
export const AUTOMATION_SUFFIX_STAGING = '@companio.space';
export const SPREAD_SHEET_EXTENSIONS = ['csv', 'xlsx', 'xls'];

export const VAT_REPORT = 'VAT';
export const OSS_REPORT = 'OSS';
export const TSD_REPORT = 'TSD';

export const UPLOAD_INVOICE_FISCAL_MONTH_OPTIONS: GenericSelectOption[] = Object.values(
  UploadSaleInvoiceOptions
).map((option) => {
  return {
    translatedLabel: option,
    value: option
  };
});

export const UPLOAD_OTHER_DOCUMENTS_FISCAL_MONTH_OPTIONS: GenericSelectOption[] = Object.values(
  UploadOtherDocumentsOptions
).map((option) => {
  return {
    translatedLabel: option,
    value: option
  };
});

export const UPLOAD_OTHER_DOCUMENT_TYPES_OPTIONS: GenericSelectOption[] = Object.values(
  UploadOtherDocumentTypes
).map((option) => {
  return {
    translatedLabel: `document_type_${option}`,
    value: option
  };
});

const PENALTY_INTERVAL_OPTIONS = ['daily', 'weekly', 'monthly', 'quarterly', 'yearly'];

export const penaltyIntervalOptions = PENALTY_INTERVAL_OPTIONS.map((interval: any) => ({
  translatedLabel: interval,
  value: interval
}));

export const invoiceTemplateOptions = Object.values(InvoiceTemplate).map((interval) => ({
  translatedLabel: interval,
  value: interval
}));

export function invoiceTemplateFakeData(template: string, contactId: string) {
  return {
    contact: contactId,
    invoice_number: 'CON-2025-0512',
    subject: 'Management Consulting Services - Q2 2025',
    issue_date: 'Mon May 26 2025',
    due_date: 'Mon Jun 16 2025',
    late_penalty: true,
    is_proforma: false,
    payment_instructions:
      'Bank Transfer Details:\nAccount Name: Strategic Insights Consulting\nBank: Global Commerce Bank\nAccount #: 1234567890\nSWIFT: GCBKUS33\n\nPlease include invoice number in the reference.',
    items: [
      {
        name: 'Business Strategy Review',
        description: 'Market analysis & growth recommendations',
        quantity: 8,
        vat: 10,
        cost: 250
      },
      {
        name: 'Operational Efficiency Audit',
        description: 'Process optimization & cost-saving analysis',
        quantity: 5,
        vat: 10,
        cost: 180
      },
      {
        name: 'Executive Coaching Sessions',
        description: 'Leadership development (4 sessions)',
        quantity: 1,
        vat: 0,
        cost: 1200
      }
    ],
    currency: 'EUR',
    total: '4,240.00',
    subtotal: '3,800.00',
    vat_amount: '440.00',
    penalty_rate: 2,
    penalty_interval: 'monthly',
    late_penalty_amount: '84.80',
    invoice_template: template,
    notes:
      'Payment due within 21 days. Late fees apply at 2% per month. Thank you for your business!'
  };
}

// Amazon CSV report columns

// General
export const AMAZON_CSV_COLUMN_TRANSACTION_EVENT_ID = 'TRANSACTION_EVENT_ID';
export const AMAZON_CSV_COLUMN_TRANSACTION_DATE = 'TRANSACTION_COMPLETE_DATE';
export const AMAZON_CSV_COLUMN_TRANSACTION_TYPE = 'TRANSACTION_TYPE';

// Item
export const AMAZON_CSV_COLUMN_ITEM_DESCRIPTION = 'ITEM_DESCRIPTION';
export const AMAZON_CSV_COLUMN_SUBTOTAL = 'TOTAL_PRICE_OF_ITEMS_AMT_VAT_EXCL';
export const AMAZON_CSV_COLUMN_VAT_PERCENTAGE_RATE = 'PRICE_OF_ITEMS_VAT_RATE_PERCENT';
export const AMAZON_CSV_COLUMN_VAT_TOTAL = 'TOTAL_PRICE_OF_ITEMS_VAT_AMT';
export const AMAZON_CSV_COLUMN_TOTAL_PRICE_WITH_VAT = 'TOTAL_PRICE_OF_ITEMS_AMT_VAT_INCL';
export const AMAZON_CSV_COLUMN_CURRENCY_CODE = 'TRANSACTION_CURRENCY_CODE';

// Buyer info
export const AMAZON_CSV_COLUMN_BUYER_VAT_NUMBER = 'BUYER_VAT_NUMBER';
export const AMAZON_CSV_COLUMN_BUYER_CITY = 'ARRIVAL_CITY';
export const AMAZON_CSV_COLUMN_BUYER_COUNTRY_CODE = 'ARRIVAL_COUNTRY';
export const AMAZON_CSV_COLUMN_BUYER_POSTAL_CODE = 'ARRIVAL_POST_CODE';
export const AMAZON_CSV_COLUMN_BUYER_NAME = 'BUYER_NAME';

// Accountancy info
export const AMAZON_CSV_COLUMN_TAX_COLLECTION_RESPONSIBILITY = 'TAX_COLLECTION_RESPONSIBILITY';
export const AMAZON_CSV_COLUMN_TAX_REPORTING_SCHEMA = 'TAX_REPORTING_SCHEME';
export const AMAZON_CSV_COLUMN_TAXABLE_JURISDICTION = 'TAXABLE_JURISDICTION';
export const AMAZON_CSV_COLUMN_VAT_CALCULATION_IMPUTATION_COUNTRY =
  'VAT_CALCULATION_IMPUTATION_COUNTRY';

export const AMAZON_CSV_REPORT_REQUIRED_COLUMNS = [
  AMAZON_CSV_COLUMN_TRANSACTION_EVENT_ID,
  AMAZON_CSV_COLUMN_TRANSACTION_DATE,
  AMAZON_CSV_COLUMN_TRANSACTION_TYPE,
  AMAZON_CSV_COLUMN_ITEM_DESCRIPTION,
  AMAZON_CSV_COLUMN_SUBTOTAL,
  AMAZON_CSV_COLUMN_VAT_PERCENTAGE_RATE,
  AMAZON_CSV_COLUMN_VAT_TOTAL,
  AMAZON_CSV_COLUMN_TOTAL_PRICE_WITH_VAT,
  AMAZON_CSV_COLUMN_CURRENCY_CODE,
  AMAZON_CSV_COLUMN_BUYER_VAT_NUMBER,
  AMAZON_CSV_COLUMN_BUYER_CITY,
  AMAZON_CSV_COLUMN_BUYER_COUNTRY_CODE,
  AMAZON_CSV_COLUMN_BUYER_POSTAL_CODE,
  AMAZON_CSV_COLUMN_BUYER_NAME,
  AMAZON_CSV_COLUMN_TAX_COLLECTION_RESPONSIBILITY,
  AMAZON_CSV_COLUMN_TAX_REPORTING_SCHEMA,
  AMAZON_CSV_COLUMN_TAXABLE_JURISDICTION,
  AMAZON_CSV_COLUMN_VAT_CALCULATION_IMPUTATION_COUNTRY
];

export const AMAZON_CSV_TRANSACTION_TYPE_SALE = 'SALE';
export const AMAZON_CSV_TRANSACTION_TYPE_LIQUIDATION_SALE = 'LIQUIDATION_SALE';
export const AMAZON_CSV_TRANSACTION_TYPE_REFUND = 'REFUND';

export const AMAZON_CSV_REPORT_SALES_TRANSACTIONS = [
  AMAZON_CSV_TRANSACTION_TYPE_SALE,
  AMAZON_CSV_TRANSACTION_TYPE_LIQUIDATION_SALE,
  AMAZON_CSV_TRANSACTION_TYPE_REFUND
];

export const AMAZON_CSV_TAX_COLLECTOR_TYPE_MARKETPLACE = 'MARKETPLACE';
export const AMAZON_CSV_TAX_COLLECTOR_TYPE_SELLER = 'SELLER';

export const AMAZON_CSV_TAX_REPORTING_SCHEMA_TYPE_REGULAR = 'REGULAR';
export const AMAZON_CSV_TAX_REPORTING_SCHEMA_TYPE_UNION_OSS = 'UNION-OSS';

export const DEFAULT_CURRENCY = 'EUR';
export const DEFAULT_CURRENCY_SYMBOL = '€';

export const PURCHASES_TABLE_KEY = 'purchases_table';
export const PURCHASES_TABLE_FILTER_KEY = 'purchases_table_filter';

export const EXPIRATION_DAYS_EMAIL_DROPBOX_NOTIFICATION = 1;

export const matchedByColor: Record<MatchedBy, string> = {
  [MatchedBy.TEAM]: defaultTheme.colors.pastelGreen,
  [MatchedBy.ACCOUNTANT]: defaultTheme.colors.pastelBlue,
  [MatchedBy.AUTO]: defaultTheme.colors.pastelPurple,
  [MatchedBy.UNMATCHED]: defaultTheme.colors.pastelRed,
  [MatchedBy.CUSTOMER]: defaultTheme.colors.pastelGreen
};

export const matchingMessage = {
  [MatchedBy.ACCOUNTANT]: 'matched_by_admin',
  [MatchedBy.TEAM]: 'matched_by_customer',
  [MatchedBy.AUTO]: 'matched_by_automatic',
  [MatchedBy.UNMATCHED]: 'matched_by_unmatched',
  [MatchedBy.CUSTOMER]: 'matched_by_customer'
};
export const ISSUE_DATE_SORT_OPTIONS: GenericSelectOption[] = [
  {
    value: SORTING_TYPE_DOCUMENT_DATE_ASC,
    translatedLabel: 'date_ascending'
  },
  {
    value: SORTING_TYPE_DOCUMENT_DATE_DESC,
    translatedLabel: 'date_descending'
  }
];

export enum DETAILS_REDIRECT_TYPE {
  SALES = 'sales',
  PURCHASES = 'purchases',
  MATCHING_DETAILS = 'matching_details'
}

export const PAYMENT_REMINDER_FREQUENCY_OPTIONS: GenericSelectOption[] = [
  {
    value: 'weekly',
    translatedLabel: 'weekly'
  },
  {
    value: 'monthly',
    translatedLabel: 'monthly'
  },
  {
    value: 'quarterly',
    translatedLabel: 'quarterly'
  }
];

export const RECURRENT_INVOICE_FREQUENCY_OPTIONS: GenericSelectOption[] = [
  {
    value: 'weekly',
    translatedLabel: 'weekly'
  },
  {
    value: 'monthly',
    translatedLabel: 'monthly'
  },
  {
    value: 'yearly',
    translatedLabel: 'yearly'
  }
];

export enum INVOICING_REDIRECT_TYPE {
  INVOICING = 'invoicing',
  SALES = 'sales',
  CUSTOMERS = 'customers'
}

export const DOCUMENT_ERROR_CODE = {
  DOCUMENT_REJECTED_DELETED_CANT_UPLOAD: 'document_rejected_deleted_cant_upload'
};

export const PURCHASES_TABLE_DATE_FILTER_KEY = 'purchases_table_date_filter';
export const PURCHASES_TABLE_LAST_PAGE_KEY = 'purchases_table_last_page';
export const SALES_TABLE_DATE_FILTER_KEY = 'sales_table_date_filter';
export const SALES_TABLE_LAST_PAGE_KEY = 'sales_table_last_page';
export const ACCOUNTING_REPORTS_TABLE_DATE_FILTER_KEY = 'reports_table_date_filter';
export const TRANSACTIONS_TABLE_DATE_FILTER_KEY = 'transactions_table_date_filter';

const specificVatRates = [0, 24];

const rangeVatRates = Array.from({ length: 11 }, (_, i) => 17 + i); // 17 to 27

export const vatOptions: GenericSelectOption[] = [
  { value: '-1', label: 'No VAT' },
  ...specificVatRates
    .sort((a, b) => a - b)
    .map((rate) => ({
      value: rate.toString(),
      label: `${rate}%`
    }))
];

export const ADVANCED_SEARCH = 'advanced_search';

export const API_ERROR_CODE_DOCUMENT_PERIOD_CLOSED = 'document_period_closed';
export const API_ERROR_CODE_MERIT_DELETE_FAILED = 'merit_delete_failed';
